import React, { useState } from 'react';

import { Dialog } from '@buildoutinc/blueprint-react/ui/Dialog';
import { Input } from '@buildoutinc/blueprint-react/ui/Input';
import { Label } from '@buildoutinc/blueprint-react/ui/Label';

import { Button } from 'components/ui/button';
import { Select } from 'components/ui/select';

import type { RoleSummary } from './types';

interface Props {
  roles: RoleSummary[];
  saving: boolean;
  onCreate: (name: string, sourceRole: RoleSummary) => void;
  onClose: () => void;
}

// A new role always starts from an existing one. There is no blank-slate option on purpose: starting from
// a role that already works means the permission set is coherent — and satisfies the dependency graph —
// before anyone touches it.
export default function NewRoleDialog({ roles, saving, onCreate, onClose }: Props) {
  const [name, setName] = useState('');
  const [source, setSource] = useState<RoleSummary | null>(null);

  return (
    <Dialog open onClose={onClose}>
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.Title>New Role</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body>
          <div className="mb-4">
            <Label htmlFor="new-role-name">Role Name</Label>
            <Input
              id="new-role-name"
              placeholder="e.g. Deal Desk"
              type="text"
              value={name}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="new-role-source">Start From</Label>
            <Select<RoleSummary>
              aria-label="Start From"
              getOptionLabel={role => role.name}
              getOptionValue={role => String(role.id)}
              inputId="new-role-source"
              options={roles}
              placeholder="Select a role to copy"
              value={source}
              onChange={role => setSource(role ?? null)}
            />
            <p className="mb-0 mt-2 text-muted fs-small">
              The new role starts with the same permissions as the one you pick. You can change them next.
            </p>
          </div>
        </Dialog.Body>
        <Dialog.Footer>
          <Dialog.Cancel>Cancel</Dialog.Cancel>
          <Button
            data-testid="create-role"
            disabled={saving || name.trim() === '' || source === null}
            onClick={() => source && onCreate(name.trim(), source)}
          >
            Create Role
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
}
